/**
 * Investment money-bucket state groups — frontend mirror of
 * `services/api/app/domain/investments/state_groups.py`.
 *
 * Which states count as committed / completed / confirmed. The canonical
 * definition is the backend's; this is the single FE source of truth so
 * presentation components never hardcode state lists inline (they used to,
 * and drifted). The backend computes the figures wherever possible — these
 * groups exist only for the few views that bucket a raw `by_state` breakdown
 * served by the backend.
 */

import { InvestmentState } from "./types";

/** Investments that never materialised / were undone — excluded from money sums. */
export const VOID_STATES: ReadonlySet<InvestmentState> = new Set([
  InvestmentState.CANCELLED,
  InvestmentState.EXPIRED,
  InvestmentState.REJECTED,
]);

/** Comprometido ("committed"): everything past the prospect stage. */
export const COMMITTED_STATES: ReadonlySet<InvestmentState> = new Set([
  InvestmentState.RESERVED,
  InvestmentState.FINANCING,
  InvestmentState.FUNDED,
  InvestmentState.DECLARED,
]);

/** Completado / "financiado": financing signed onwards. */
export const COMPLETED_STATES: ReadonlySet<InvestmentState> = new Set([
  InvestmentState.FINANCING,
  InvestmentState.FUNDED,
  InvestmentState.DECLARED,
]);

/** Confirmado ("cobrado"): money received. FINANCING is committed, not confirmed. */
export const CONFIRMED_STATES: ReadonlySet<InvestmentState> = new Set([
  InvestmentState.FUNDED,
  InvestmentState.DECLARED,
]);

export type LiveInvestmentState = Exclude<InvestmentState, "cancelled" | "expired" | "rejected">;

const isLiveState = (state: InvestmentState): state is LiveInvestmentState =>
  !VOID_STATES.has(state);

export const LIVE_PIPELINE_STATES: readonly LiveInvestmentState[] =
  Object.values(InvestmentState).filter(isLiveState);

/** True if a (possibly raw-string) state belongs to the given group. */
export const isInGroup = (state: string, group: ReadonlySet<InvestmentState>): boolean =>
  group.has(state as InvestmentState);
